import React, { useState, useRef, useCallback, useEffect } from 'react';
import { linkify } from 'util/stringUtils';
import classnames from 'classnames';
import _ from 'lodash';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube, faVimeoV } from '@fortawesome/free-brands-svg-icons';
import {
  faUpload,
  faImage,
  faCloudUploadAlt,
} from '@fortawesome/free-solid-svg-icons';

import ImageUploadModal from 'components/modals/ImageUploadModal/ImageUploadModal';
import CauzeVideo from 'components/CauzeVideo/CauzeVideo';
import placeholder from 'assets/images/placeholders/placeholder-image.svg';

import {
  compressForUpload,
  generateVideoThumbnail,
  getVideoDurationFromFile,
  urlToImageFile,
} from 'util/image';
import { MAX_VIDEO_DURATION_SECONDS } from 'constants/video';
import useMobile from 'hooks/useMobile';
import useCauzeStore from 'stores/CauzeStoreV2';
import {
  getVimeoThumbnail,
  getVimeoVideoId,
  getYoutubeThumbnail,
  getYoutubeVideoId,
} from 'util/formFieldValidators';

const scrubLink = (text) => {
  const links = linkify.find(text);
  if (links.length > 0 && links[0].href) {
    return links[0].href;
  }
  return '';
};

const ImageUploadTypeButton = ({ uploadType, isSelected, onClick }) => {
  const renderTitle = () => {
    if (uploadType === 'default') {
      return 'Default Image';
    } else if (uploadType === 'upload') {
      return 'Upload Image/Video';
    } else if (uploadType === 'youtube') {
      return 'YouTube/Vimeo';
    }
  };

  const renderSubtitle = () => {
    if (uploadType === 'default') {
      return 'Use built-in cover';
    } else if (uploadType === 'upload') {
      return 'Upload an image or video (MP4, JPG, PNG)';
    } else if (uploadType === 'youtube') {
      return 'Embed a video from YouTube or Vimeo';
    }
  };

  const renderIcon = () => {
    if (uploadType === 'default') {
      return (
        <FontAwesomeIcon
          className="text-gray-949 mb-2"
          size="4x"
          icon={faImage}
        />
      );
    } else if (uploadType === 'upload') {
      return (
        <FontAwesomeIcon
          className="text-gray-949 mb-2"
          size="4x"
          icon={faCloudUploadAlt}
        />
      );
    } else if (uploadType === 'youtube') {
      return (
        <div className="flex justify-center gap-2 w-full mb-2">
          <FontAwesomeIcon
            className="text-gray-949"
            size="4x"
            icon={faYoutube}
          />
          <FontAwesomeIcon
            className="text-gray-949 hidden lg:block"
            size="4x"
            icon={faVimeoV}
          />
        </div>
      );
    }
  };

  return (
    <button
      type="button"
      className={classnames(
        'flex-1 min-w-0 pt-4 px-4 pb-2 flex flex-col bg-white hover:bg-lightgray-f7f active:bg-lightgray-f1f border border-solid rounded-none lg:rounded-[10px] border-lightgray-c4c items-center cursor-pointer',
        {
          'border-2 !border-cauze-light': isSelected,
        },
      )}
      onClick={onClick}
    >
      {renderIcon()}
      <div className="font-agenda-semibold text-lg">{renderTitle()}</div>
      <div className="font-agenda-medium text-text-light text-sm">
        {renderSubtitle()}
      </div>
    </button>
  );
};

const FileUploadZone = ({
  onSelectFile,
  hasUploadedFile,
  hasUploadedVideo,
  vertical,
}) => {
  const onDrop = useCallback(
    (acceptedFiles) => {
      onSelectFile(acceptedFiles);
    },
    [onSelectFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png'],
      'video/*': ['.mp4'],
    },
    maxFiles: 1,
  });

  return (
    <div
      {...getRootProps()}
      className={classnames(
        'group cursor-pointer z-10 w-full rounded-[10px] border border-lightgray-c4c transition-all duration-100',
        {
          'hover:bg-[#f7f7f7aa] border-none': hasUploadedFile,
          'border-dashed py-12': !hasUploadedFile,
          relative: !hasUploadedFile,
          'absolute top-0 left-0 bottom-0 !rounded-b-none': hasUploadedFile,
          'h-full': !hasUploadedVideo,
          'h-[92.5%]': hasUploadedVideo && vertical,
          'h-[90%]': hasUploadedVideo && !vertical,
        },
      )}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col gap-4 items-center justify-center w-full h-full">
        <div
          className={classnames('flex gap-8 transition-all duration-100', {
            'opacity-0 group-hover:opacity-100': hasUploadedFile,
            'opacity-100': !hasUploadedFile,
            'flex-col items-center text-center': hasUploadedFile || vertical,
          })}
        >
          <FontAwesomeIcon
            icon={faUpload}
            size="3x"
            className={classnames('', {
              'text-gray-949': !hasUploadedFile,
              'text-text-light': hasUploadedFile,
            })}
          />
          <div className="select-none flex flex-col justify-center">
            {isDragActive ? (
              <p className="font-agenda-semibold text-2xl">
                Drop your files here...
              </p>
            ) : (
              <>
                <p className="font-agenda-semibold text-2xl">
                  Drag & drop or click to{' '}
                  {hasUploadedFile ? 'replace' : 'upload'}
                </p>
                <p className="font-agenda-medium text-text-light text-lg">
                  (Supported: JPG, PNG, MP4)
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CauzeImageOrVideo = ({
  currentCauze,
  onToggleClose,
  isActive,
  setIsChanged,
  uiStore,
}) => {
  const cauzeStore = useCauzeStore();
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [activeUploadType, setActiveUploadType] = useState('default');
  const [videoUrl, setVideoUrl] = useState(currentCauze.previewUrl);
  const [imageUrl, setImageUrl] = useState(currentCauze.previewImageUrl);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [finalImageUrl, setFinalImageUrl] = useState(imageUrl);
  const [showFileError, setShowFileError] = useState(null);
  const cardBodyRef = useRef(null);
  const { isLaptop } = useMobile();

  const scrollToBottom = () => {
    setTimeout(() => {
      if (cardBodyRef.current) {
        cardBodyRef.current.scrollTo({
          top: cardBodyRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const onSelectFile = async (files) => {
    if (files && files.length > 0) {
      const targetFile = files[0];

      setUploadedVideo(null);
      setFinalImageUrl(null);
      setImageUrl(null);

      if (targetFile.type.includes('video')) {
        const duration = await getVideoDurationFromFile(targetFile);

        if (duration > MAX_VIDEO_DURATION_SECONDS) {
          uiStore.showNotification({
            body: 'Video uploads must be under 2 minutes.',
            type: 'ERROR',
          });
          return;
        }

        generateVideoThumbnail(targetFile, async ({ image, width, height }) => {
          setUploadedVideo({
            video: targetFile,
            width,
            height,
            thumbnail: image,
          });

          scrollToBottom();
        });
      } else {
        setIsChanged(true);
        setShowImageUploadModal(true);
        const reader = new FileReader();
        reader.addEventListener('load', () => setImageUrl(reader.result));
        reader.readAsDataURL(await compressForUpload(files[0]));
      }
    }
  };

  const handleSubmit = async () => {
    if (activeUploadType === 'default') {
      cauzeStore.updateCauze({
        previewImageUrl: '',
        imageId: 0,
        externalImageUrl: '',
        previewUrl: '',
      });
    } else if (activeUploadType === 'upload') {
      cauzeStore.setLoading(true);

      if (uploadedVideo) {
        cauzeStore.updateCauze({
          previewImageUrl: uploadedVideo.thumbnail,
          externalImageUrl: '',
          imageId: 0,
          previewUrl: '',
        });

        await Promise.all([
          urlToImageFile(uploadedVideo.thumbnail).then((imgFile) =>
            cauzeStore.uploadImage(imgFile),
          ),

          cauzeStore.startVideoUpload(uploadedVideo),
        ]);
      } else if (finalImageUrl) {
        await urlToImageFile(finalImageUrl).then(async (imgFile) =>
          cauzeStore.uploadImage(imgFile),
        );

        cauzeStore.updateCauze({
          previewImageUrl: finalImageUrl,
          externalImageUrl: '',
          previewUrl: '',
        });
      }

      cauzeStore.setLoading(false);
    } else if (activeUploadType === 'youtube') {
      const youtubeVideoId = getYoutubeVideoId(videoUrl);
      const vimeoVideoId = getVimeoVideoId(videoUrl);
      let externalImageUrl = getYoutubeThumbnail(youtubeVideoId);

      if (!externalImageUrl && vimeoVideoId) {
        externalImageUrl = await getVimeoThumbnail(vimeoVideoId);
      }

      cauzeStore.updateCauze({
        previewUrl: videoUrl,
        previewImageUrl: '',
        imageId: 0,
        externalImageUrl: externalImageUrl,
      });
    }

    onToggleClose();
  };

  useEffect(() => {
    if (isActive) {
      if (currentCauze.previewImageUrl) {
        setActiveUploadType('upload');
      } else if (currentCauze.previewUrl) {
        setActiveUploadType('youtube');
      } else {
        setActiveUploadType('default');
      }
    }
  }, [isActive]);

  return (
    <>
      <ImageUploadModal
        updateImageUrl={(newImageUrl) => {
          setFinalImageUrl(newImageUrl);
        }}
        onSave={() => setShowImageUploadModal(false)}
        isActive={showImageUploadModal}
        onToggleClose={() => setShowImageUploadModal(false)}
        src={placeholder}
        isChanged={true}
        setIsChanged={setIsChanged}
        showFileError={showFileError}
        setShowFileError={setShowFileError}
        upImg={imageUrl}
        setUpImg={setImageUrl}
      />
      <div
        className={classnames('modal image-upload-modal !z-30', {
          'is-active': isActive,
        })}
      >
        <div onClick={onToggleClose} className="modal-background"></div>
        <div className="modal-card bg-white rounded-none lg:rounded-[10px]">
          <header className="flex justify-between pt-8 pl-8 relative">
            <div className="text-left font-agenda-semibold text-3xl my-auto leading-none pt-1">
              Cauze Image or Video
            </div>
            <button
              className="delete absolute top-0 right-0"
              type="button"
              aria-label="close"
              onClick={onToggleClose}
            ></button>
          </header>
          <section
            ref={cardBodyRef}
            className="modal-card-body !pt-0 !px-1 lg:!px-8 !pb-0"
          >
            <h3 className="font-agenda text-text-light text-xl py-3 text-center lg:text-left">
              Select how you'd like to set your cauze image or video
            </h3>
            <div className="flex flex-col lg:flex-row gap-0 lg:gap-5">
              <ImageUploadTypeButton
                uploadType="default"
                onClick={() => {
                  setActiveUploadType('default');
                  scrollToBottom();
                }}
                isSelected={activeUploadType === 'default'}
              />
              <ImageUploadTypeButton
                uploadType="upload"
                onClick={() => {
                  setActiveUploadType('upload');
                  scrollToBottom();
                }}
                isSelected={activeUploadType === 'upload'}
              />
              <ImageUploadTypeButton
                uploadType="youtube"
                isSelected={activeUploadType === 'youtube'}
                onClick={() => {
                  setActiveUploadType('youtube');
                  cardBodyRef.current.scrollTo({
                    top: cardBodyRef.current.scrollHeight,
                    behavior: 'smooth',
                  });
                }}
              />
            </div>

            <div className="mt-5">
              {activeUploadType === 'default' && (
                <div className="h-60 lg:h-[359px] w-full rounded-[10px] linear-gradient-image"></div>
              )}
              {activeUploadType === 'upload' && (
                <div className="relative w-full h-auto overflow-hidden">
                  <div className="flex justify-center">
                    <div
                      className={classnames(
                        'relative overflow-hidden rounded-[10px]',
                        {
                          'flex-1': !finalImageUrl && !uploadedVideo,
                        },
                      )}
                    >
                      {finalImageUrl && (
                        <img
                          className="w-full rounded-[10px] object-cover"
                          src={finalImageUrl}
                        />
                      )}
                      {uploadedVideo && (
                        <CauzeVideo
                          video={{
                            videoUrl: URL.createObjectURL(uploadedVideo.video),
                            width: uploadedVideo.width,
                            height: uploadedVideo.height,
                          }}
                          maxWidth={Math.min(
                            document.body.clientWidth - 8,
                            641,
                          )}
                          maxHeight={600}
                          videoName={`${currentCauze.name}'s Video`}
                          isModal={true}
                          autoplay={false}
                        />
                      )}
                      <FileUploadZone
                        onSelectFile={(files) => onSelectFile(files)}
                        hasUploadedFile={
                          Boolean(finalImageUrl) || Boolean(uploadedVideo)
                        }
                        hasUploadedVideo={Boolean(uploadedVideo)}
                        vertical={
                          uploadedVideo?.height > uploadedVideo?.width ||
                          isLaptop
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
              {activeUploadType === 'youtube' && (
                <div className="flex-column h-full">
                  <div>
                    <input
                      type="text"
                      name="video"
                      className="border mb-5 border-solid border-lightgray-c4c rounded-[10px] p-2 pt-[12px] w-full focus:outline-cauze-light"
                      value={videoUrl}
                      placeholder={'Youtube or Vimeo Video Link'}
                      onChange={(e) => {
                        const link = scrubLink(e.target.value);
                        setVideoUrl(link);
                      }}
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    {Boolean(videoUrl) && (
                      <CauzeVideo
                        className="flex-1"
                        video={{
                          videoUrl: videoUrl,
                          width: 1280,
                          height: 720,
                        }}
                        maxWidth={641}
                        videoName={`${currentCauze.name}'s Video`}
                        isModal={true}
                        autoplay={false}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          <footer className="modal-card-foot !bg-white border-none">
            <div className="flex w-full justify-evenly lg:justify-end gap-2">
              <button
                type="button"
                className="cz-btn-outline black flex-1 lg:flex-none"
                onClick={() => onToggleClose()}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cz-btn-brand-dark flex-1 lg:flex-none"
                onClick={handleSubmit}
              >
                Save Changes
              </button>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
};

export default CauzeImageOrVideo;
